import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Divider,
  Grid,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  FormatListBulleted as ListIcon,
  Email as EmailIcon,
  Forum as SlackIcon,
  NotificationsActive as PagerDutyIcon,
  Groups as TeamsIcon,
  Code as WebhookCodeIcon,
  Notifications as NotificationsIcon,
  VerifiedUser as VerifiedIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  GppBad as UnverifiedIcon,
} from '@mui/icons-material';
import {
  NotificationChannelCardProps,
  NotificationChannelType,
  getCardBorder,
  CARD_BORDER_RADIUS,
} from '../types';
import { ChannelVerificationButton } from './ChannelVerificationButton';

/**
 * Helper: Get icon for notification channel type
 */
const getChannelTypeIcon = (type: NotificationChannelType): React.ReactNode => {
  const icons: Record<NotificationChannelType, React.ReactNode> = {
    canmail: <ListIcon />,
    email: <EmailIcon />,
    slack: <SlackIcon />,
    pagerduty: <PagerDutyIcon />,
    teams: <TeamsIcon />,
    webhook: <WebhookCodeIcon />,
  };
  return icons[type] || <NotificationsIcon />;
};

/**
 * Helper: Get display label for channel type
 */
const getChannelTypeLabel = (
  type: NotificationChannelType,
  memberCount?: number
): string => {
  const labels: Record<NotificationChannelType, string> = {
    canmail: `CANmail List${memberCount ? ` (${memberCount})` : ''}`,
    email: 'Individual Email',
    slack: 'Slack Channel',
    pagerduty: 'PagerDuty Service',
    teams: 'Microsoft Teams',
    webhook: 'Webhook',
  };
  return labels[type] || 'Unknown';
};

/**
 * NotificationChannelCard - Displays a single notification channel configuration.
 *
 * Shows:
 * - Channel type icon and name
 * - Verified status badge
 * - What exporters it watches (ALL or specific list)
 * - Which severities trigger notifications
 * - Edit/Delete actions
 */
export function NotificationChannelCard({
  channel,
  onEdit,
  onDelete,
  onTest,
  onVerified,
}: NotificationChannelCardProps) {
  const theme = useTheme();

  const severityIcons: Record<string, { icon: React.ReactNode; color: string }> = {
    critical: {
      icon: <ErrorIcon sx={{ fontSize: 14 }} />,
      color: theme.palette.error.main,
    },
    warning: {
      icon: <WarningIcon sx={{ fontSize: 14 }} />,
      color: theme.palette.warning.main,
    },
    info: {
      icon: <InfoIcon sx={{ fontSize: 14 }} />,
      color: theme.palette.info.main,
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: getCardBorder(theme),
        borderRadius: CARD_BORDER_RADIUS,
        overflow: 'hidden',
        mb: 2,
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
        },
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              width: 40,
              height: 40,
            }}
          >
            {getChannelTypeIcon(channel.type)}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {channel.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getChannelTypeLabel(channel.type, channel.memberCount)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {channel.verified ? (
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
              label="Verified"
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 24, '& .MuiChip-icon': { fontSize: 14 } }}
            />
          ) : (
            <Tooltip title="This channel has not been verified yet">
              <Chip
                icon={<UnverifiedIcon sx={{ fontSize: 14 }} />}
                label="Unverified"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 24, '& .MuiChip-icon': { fontSize: 14 } }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Card Content */}
      <Divider />
      <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.grey[500], 0.02) }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              Watches
            </Typography>
            {channel.watchAll ? (
              <Chip
                label="ALL exporters"
                size="small"
                color="primary"
                variant="outlined"
              />
            ) : (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {channel.watchExporters.slice(0, 3).map((exp) => (
                  <Chip key={exp} label={exp} size="small" variant="outlined" />
                ))}
                {channel.watchExporters.length > 3 && (
                  <Chip
                    label={`+${channel.watchExporters.length - 3} more`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5 }}
            >
              Severities
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {channel.severities.map((sev) => (
                <Chip
                  key={sev}
                  icon={severityIcons[sev].icon as React.ReactElement}
                  label={sev.charAt(0).toUpperCase() + sev.slice(1)}
                  size="small"
                  sx={{
                    bgcolor: alpha(severityIcons[sev].color, 0.1),
                    color: severityIcons[sev].color,
                    '& .MuiChip-icon': { color: severityIcons[sev].color },
                  }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Card Actions */}
      <Divider />
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          {onTest && (
            <ChannelVerificationButton
              channelId={channel.id}
              channelName={channel.name}
              isVerified={channel.verified}
              onTest={onTest}
              onVerified={onVerified}
              variant="button"
              size="small"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onEdit && (
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => onEdit(channel)}
            >
              Edit
            </Button>
          )}
          {onDelete && (
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(channel)}
              sx={{ '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default NotificationChannelCard;
